import { useRef, useMemo } from 'react'
import { Image, Plus } from 'lucide-react'
import FileUpload from '../UI/FileUpload'
import SectionBlock from '../UI/SectionBlock'
import SectionHeader from '../UI/SectionHeader'
import { MEDIA_TYPES, SENSITIVE_MEDIA_TYPES } from '../../utils/mediaTypeUtils'

const MediaSection = ({ talent, talentId, hasPermission, setTalent }) => {
  const mediaRef = useRef(null)

  // Пер-action списки типів. Для звичайних типів рішає generic `${type}_media`;
  // для sensitive (contract, …) — тільки специфічний `${type}_media_${sensitive}`.
  const { allowedReadTypes, allowedCreateTypes, allowedUpdateTypes, allowedDeleteTypes } = useMemo(() => {
    const talentType = talent.talent_type
    const check = (action) => {
      const genericAllowed = hasPermission('talent-service', `${talentType}_media`, action)
      const result = []
      for (const type of Object.values(MEDIA_TYPES)) {
        if (SENSITIVE_MEDIA_TYPES.includes(type)) {
          if (hasPermission('talent-service', `${talentType}_media_${type}`, action)) result.push(type)
        } else if (genericAllowed) {
          result.push(type)
        }
      }
      return result
    }
    return {
      allowedReadTypes: check('read'),
      allowedCreateTypes: check('create'),
      allowedUpdateTypes: check('update'),
      allowedDeleteTypes: check('delete'),
    }
  }, [talent.talent_type, hasPermission])

  const canCreateAny = allowedCreateTypes.length > 0
  const canUpdateAny = allowedUpdateTypes.length > 0
  const canDeleteAny = allowedDeleteTypes.length > 0

  return (
    <SectionBlock id="media">
      <SectionHeader
        icon={Image}
        title="Media"
        action={canCreateAny ? (
          <button
            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-primary-600 text-white text-xs font-medium rounded-lg hover:bg-primary-700 dark:bg-primary-500 dark:hover:bg-primary-600 transition-colors"
            onClick={() => mediaRef.current?.openUpload()}
          >
            <Plus className="h-3.5 w-3.5" /> Add
          </button>
        ) : null}
      />
      <FileUpload
        ref={mediaRef}
        talentId={talentId}
        existingFiles={talent.media || []}
        maxFiles={20}
        allowedTypes={['image/*', 'application/pdf', 'video/*', 'audio/*']}
        allowedMediaTypes={allowedReadTypes}
        allowedCreateTypes={allowedCreateTypes}
        allowedUpdateTypes={allowedUpdateTypes}
        allowedDeleteTypes={allowedDeleteTypes}
        onFileUploaded={newMedia => setTalent(prev => ({ ...prev, media: [...(prev.media || []), newMedia] }))}
        onFileDeleted={deletedFileId => setTalent(prev => ({ ...prev, media: prev.media.filter(m => m.id !== deletedFileId) }))}
        onFileUpdated={updatedFile => setTalent(prev => ({ ...prev, media: prev.media.map(m => m.id === updatedFile.id ? updatedFile : m) }))}
        canCreate={canCreateAny}
        canUpdate={canUpdateAny}
        canDelete={canDeleteAny}
        showAddButton={false}
      />
    </SectionBlock>
  )
}

export default MediaSection
